import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { LABEL_STATUS } from 'services/labels/types';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import { STATUS } from 'services/types';
import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';
import { IStandardEnum } from 'types/global/enum.interface';
import { enumToObject } from 'utils/global/object.utils';

export const useEnumToSelectOptions = () => {
  const { t } = useTranslation();
  const licenceConfiguration = useGetLicenceConfiguration();
  const clientModuleAccess = licenceConfiguration.data?.clientModuleAccess ?? false;

  const getSelectOptions = <T>(
    ENUM: IStandardEnum<T>,
    i18nextPrefix?: string,
    options?: { includeLabelStatusBanned?: boolean }
  ) =>
    enumToObject<T>(ENUM)
      .filter(object => object.value != -1)
      .filter(
        object =>
          options?.includeLabelStatusBanned ||
          (ENUM as unknown) !== LABEL_STATUS ||
          (object.value as number) !== LABEL_STATUS.Banned
      )
      .map(object => ({
        label: i18nextPrefix ? t(`${i18nextPrefix}.${object.key}`) : object.key,
        value: object.value,
      }));

  const getStatusOptions = (
    ENUM: IStandardEnum<number>,
    initialStatus: number,
    i18nextPrefix: string
  ) => {
    let selectOptions = getSelectOptions(ENUM, i18nextPrefix);

    if (initialStatus !== 1) {
      selectOptions = selectOptions.filter(option => option.value !== 1);
    }

    if (ENUM === LABEL_STATUS) {
      selectOptions = selectOptions.filter(
        option => option.value !== LABEL_STATUS.Banned
      );
    }

    if (!clientModuleAccess) {
      if (ENUM === LABEL_STATUS) {
        selectOptions = selectOptions.filter(
          option =>
            option.value !== LABEL_STATUS.NotVerified &&
            option.value !== LABEL_STATUS.DuringVerification
        );
      }
      else if (ENUM === STATUS) {
        selectOptions = selectOptions.filter(
          option =>
            option.value !== STATUS.notVerified &&
            option.value !== STATUS.inVerification
        );
      }
    }

    return selectOptions;
  };

  const getAgreementTypeOptions = (
    ENUM: IStandardEnum<number>,
    i18nextPrefix: string
  ) => {
    const selectOptions = getSelectOptions(ENUM, i18nextPrefix);
    return selectOptions.filter(
      option =>
        option.value !== AGREEMENT_TYPE.shares &&
        option.value !== AGREEMENT_TYPE.defaultShare
    );
  };

  return {
    getSelectOptions,
    getStatusOptions,
    getAgreementTypeOptions,
  };
};

export const getYearsSelectOptions = (yearsBack?: number) => {
  const array = new Array(yearsBack ?? 100).fill({});
  return array.map((value, index) => ({
    value: new Date().getFullYear() - index,
    label: new Date().getFullYear() - index,
  }));
};

export const booleanSelectOptions = [
  { label: t('input.option.yes'), value: true },
  { label: t('input.option.no'), value: false },
];
